import { Button, ProgressBar } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCentrifugeProgress } from '@/hooks/useCentrifugeProgress';

type ClusteringProcessingStateProps = {
  taskId?: string;
  onRefresh?: () => void;
  onViewDemo?: () => void;
  isRefreshing?: boolean;
};

/** Status labels aligned with MassiveStatusRenderer / clustering history table */
function getClusteringStatusLabel(status: number | undefined): string {
  switch (status) {
    case 2:
      return 'در صف خوشه‌بندی';
    case 4:
      return 'در حال خوشه‌بندی';
    case 6:
      return 'پایان (موفق)';
    case 3:
      return 'خطا';
    case 49:
      return 'متوقف شده';
    default:
      return 'در حال پردازش خوشه‌بندی';
  }
}

const ClusteringProcessingState = ({
  taskId,
  onRefresh,
  onViewDemo,
  isRefreshing = false
}: ClusteringProcessingStateProps) => {
  const { connected, progressPercentage, status, isComplete } =
    useCentrifugeProgress({
      channelId: taskId,
      active: Boolean(taskId),
      // onProgress: data => {
      //   console.log('[Clustering] socket progress:', data);
      // },
      onComplete: () => {
        onRefresh?.();
      }
    });

  const handleRefresh = () => {
    if (onRefresh) {
      onRefresh();
      return;
    }
    window.location.reload();
  };

  const statusLabel = getClusteringStatusLabel(status);
  const showProgress =
    connected && (status === 4 || progressPercentage > 0 || status === undefined);
  const isErrorOrStopped = status === 3 || status === 49;

  return (
    <div
      className="d-flex flex-column align-items-center text-center px-3 pt-2 pb-4"
      role="status"
      aria-live="polite"
    >
      <style>{`
        @keyframes clustering-dot {
          0%, 80%, 100% { opacity: 0.25; transform: translateY(0); }
          40% { opacity: 1; transform: translateY(-3px); }
        }
        .clustering-processing-dots span {
          display: inline-block;
          width: 0.35rem;
          height: 0.35rem;
          margin: 0 0.15rem;
          border-radius: 50%;
          background: var(--falcon-primary);
          animation: clustering-dot 1.2s ease-in-out infinite;
        }
        .clustering-processing-dots span:nth-child(2) { animation-delay: 0.15s; }
        .clustering-processing-dots span:nth-child(3) { animation-delay: 0.3s; }
        html:not([data-bs-theme='dark']) .clustering-processing-panel {
          border-color: var(--falcon-gray-200) !important;
        }
        html[data-bs-theme='dark'] .clustering-processing-panel {
          border-color: var(--falcon-gray-200) !important;
        }
      `}</style>

      <div
        className="clustering-processing-panel rounded-3 border border-400 bg-body-tertiary shadow-sm p-4"
        style={{ width: 'min(100%, 36rem)' }}
      >
        <h3 className="fs-0 fw-semibold text-1000 mb-3">
          {statusLabel}
          {!isErrorOrStopped && !isComplete ? (
            <span className="clustering-processing-dots ms-1" aria-hidden>
              <span />
              <span />
              <span />
            </span>
          ) : null}
        </h3>

        <p
          className="fs--1 text-500 lh-base mb-4 mx-auto"
          style={{ maxWidth: '24rem' }}
        >
          پردازش داده‌ها ممکن است بین ۱۰ دقیقه تا ۲ ساعت طول بکشد. پس از اتمام،
          همین صفحه را بررسی کنید تا نتیجه را ببینید. سپاس از صبر شما.
        </p>

        <div className="mb-4 text-start">
          {!connected && taskId && !isComplete ? (
            <div className="fs--1 text-500 mb-2">
              در حال اتصال برای دریافت وضعیت پردازش...
            </div>
          ) : null}
          {status === 2 ? (
            <div className="fs--1 text-500 mb-2">
              در صف پردازش؛ لطفاً منتظر بمانید...
            </div>
          ) : null}
          {showProgress && !isErrorOrStopped ? (
            <>
              <div
                className="d-flex justify-content-between mb-1"
                style={{ fontSize: '14px' }}
              >
                <span className="text-700">میزان پیشرفت</span>
                <span className="fw-medium">{progressPercentage}%</span>
              </div>
              <ProgressBar
                now={progressPercentage}
                max={100}
                style={{ height: 5 }}
                className="rounded-pill align-middle"
                variant={isComplete ? 'success' : 'primary'}
              />
            </>
          ) : (
            <div
              className="w-100 rounded-pill bg-200"
              style={{ height: 5 }}
              aria-hidden
            />
          )}
        </div>

        <div className="d-flex flex-wrap justify-content-center gap-3 mb-4">
          <span className="badge rounded-pill border border-400 bg-100 text-700 fw-medium">
            <FontAwesomeIcon icon="clock" className="me-1" />
            ۱۰ دقیقه تا ۲ ساعت
          </span>
          <span className="badge rounded-pill border border-400 bg-100 text-700 fw-medium">
            <FontAwesomeIcon icon="sync-alt" className="me-1" />
            پس از اتمام بررسی کنید
          </span>
        </div>

        <div className="d-flex flex-column align-items-center gap-2">
          <Button
            variant="falcon-primary"
            size="sm"
            className="px-3"
            onClick={handleRefresh}
            disabled={isRefreshing}
          >
            <FontAwesomeIcon
              icon="redo"
              className={`me-2${isRefreshing ? ' fa-spin' : ''}`}
            />
            {isRefreshing ? 'در حال بررسی...' : 'چک کن ببین نتیجه اومد'}
          </Button>
          {onViewDemo ? (
            <a
              href="#demo"
              className={`fs--2 text-decoration-underline${isRefreshing ? ' disabled' : ''}`}
              onClick={event => {
                event.preventDefault();
                if (!isRefreshing) onViewDemo();
              }}
              aria-disabled={isRefreshing || undefined}
            >
              دمو
            </a>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default ClusteringProcessingState;
